import { FC, useEffect, useState } from 'react';
import { AriaButton } from 'src/components/Button/Button';
import { useI18nContext } from 'src/i18n/i18n-react';
import useNewAnalyticsEvent from 'src/utils/useNewAnalyticsEvent';
import { FacebookAuthState, useFacebook } from 'src/utils/useFacebook';
import { InsightsAdAccountSelectorList } from 'src/pages/Insights/components/InsightsAdAccountSelectorList/InsightsAdAccountSelectorList';
import { Alert } from '../Alert';
import AriaModal from '../AriaModal/AriaModal';
import { SquareLoaders } from '../SquareLoaders';

type Props = {
  facebookAuthState: FacebookAuthState;
  onClose: () => void;
};

export const FacebookAdAccountSelectModal: FC<Props> = ({
  facebookAuthState,
  onClose,
}) => {
  const [selectedAdAccountIds, setSelectedAdAccountIds] = useState<string[]>(
    []
  );
  const { LL } = useI18nContext();
  const { recordEvent } = useNewAnalyticsEvent();

  const { updateIntegration: updateFacebookIntegration } = useFacebook();

  useEffect(() => {
    if (facebookAuthState.status === 'success') {
      void recordEvent({
        action: 'Post-FB Auth - Accounts Listed',
        target: 'Insights Account',
        metadata: {
          accounts: facebookAuthState.adAccounts
            .map((adAccount) => `${adAccount.accountId}|${adAccount.name}`)
            .join(', '),
        },
      });
    }
  }, [facebookAuthState, recordEvent]);

  return (
    <AriaModal
      show={facebookAuthState.status !== 'init'}
      onClose={onClose}
      title="Select Facebook Ad Accounts"
      className="!max-w-xl"
      footer={
        facebookAuthState.status === 'success' && (
          <div className="flex flex-row justify-between gap-4">
            <AriaButton
              className="shrink-0 grow"
              isDisabled={updateFacebookIntegration.isLoading}
              variant="secondary"
              onPress={onClose}
            >
              {LL.cancel()}
            </AriaButton>
            <AriaButton
              className="shrink-0 grow"
              loading={updateFacebookIntegration.isLoading}
              onPress={async () => {
                const selectedAdAccounts = facebookAuthState.adAccounts.filter(
                  (x) => selectedAdAccountIds.includes(x.accountId)
                );

                await updateFacebookIntegration.mutateAsync({
                  fbUserID: facebookAuthState.fbUserID,
                  accessToken: facebookAuthState.accessToken,
                  adAccounts: selectedAdAccounts,
                });

                void recordEvent({
                  action: 'Post-FB Auth - Accounts Connected',
                  target: 'Insights Account',
                  metadata: {
                    accounts: facebookAuthState.adAccounts
                      .map(
                        (adAccount) =>
                          `${adAccount.accountId}|${adAccount.name}`
                      )
                      .join(', '),
                  },
                });

                onClose();
              }}
              isDisabled={selectedAdAccountIds.length === 0}
            >
              {LL.confirm()}
            </AriaButton>
          </div>
        )
      }
    >
      {facebookAuthState.status === 'error' && (
        <Alert className="w-full" type="error">
          <p className="text-xs font-normal">
            {LL.errors.genericWithDetail({
              detail: facebookAuthState.error.message ?? 'Unknown error',
            })}
          </p>
        </Alert>
      )}
      {facebookAuthState.status === 'pending' && (
        <div className="flex flex-col gap-3">
          <SquareLoaders className="h-10" amount={5} />
        </div>
      )}
      {facebookAuthState.status === 'success' && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-normal text-primary">
            {LL.insights.accounts.accountSelect.description()}
          </p>

          <InsightsAdAccountSelectorList
            label={LL.insights.accounts.accountSelect.availableTitle()}
            advertiserAccounts={facebookAuthState.adAccounts}
            selectedAdvertiserIds={selectedAdAccountIds}
            setSelectedAdvertiserIds={setSelectedAdAccountIds}
            platform="facebook"
          />
        </div>
      )}
    </AriaModal>
  );
};
